import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../css/chart-main.css";
import "../../css/sector-materials.css";
import { useSectorMaterials, useSectors } from "../../hooks";
import { interactionService } from "../../services";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { formatCurrency, formatPerUnit, formatUnit } from "../../utils/formatUnit";
import { translateSectorLabel } from "../../utils/sectorLabel";

// Mock/Fallback data for materials in a sector (used when API is not available)
const mockMaterialsData = {
  lists: [
    {
      id: 1,
      name: "LIST 1",
      materials: [
        {
          id: 1,
          code: "MAT_1",
          name: "Autoglas Beligen (inkl. Steuern und Abgaben)",
          date: "27.04.2025",
          change: "+1.25 %",
          change_pct: 1.25,
          value: "3,670.35",
          highDisplay: "3,670.35",
          lowDisplay: "3,670.35",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
        {
          id: 2,
          code: "MAT_2",
          name: "Autoglas Reparatur (inkl. Steuern und Abgaben)",
          date: "15.05.2025",
          change: "+1.50 %",
          change_pct: 1.50,
          value: "2,250.00",
          highDisplay: "2,250.00",
          lowDisplay: "2,250.00",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
        {
          id: 3,
          code: "MAT_3",
          name: "Fahrzeugbeschriftung (inkl. Steuern und Abgaben)",
          date: "10.06.2025",
          change: "+2.00 %",
          change_pct: 2.00,
          value: "1,800.00",
          highDisplay: "1,800.00",
          lowDisplay: "1,800.00",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
        {
          id: 4,
          code: "MAT_4",
          name: "Reifenschutz (inkl. Steuern und Abgaben)",
          date: "21.07.2025",
          change: "+1.25 %",
          change_pct: 1.25,
          value: "1,000.00",
          highDisplay: "1,000.00",
          lowDisplay: "1,000.00",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
        {
          id: 5,
          code: "MAT_5",
          name: "Ölwechsel Service (inkl. Steuern und Abgaben)",
          date: "30.08.2025",
          change: "+1.90 %",
          change_pct: 1.90,
          value: "500.00",
          highDisplay: "500.00",
          lowDisplay: "500.00",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
        {
          id: 6,
          code: "MAT_6",
          name: "Karosseriearbeit (inkl. Steuern und Abgaben)",
          date: "05.09.2025",
          change: "+2.50 %",
          change_pct: 2.50,
          value: "4,200.00",
          highDisplay: "4,200.00",
          lowDisplay: "4,200.00",
          currencyUnit: "EUR / mt",
          currency: "EUR",
          unit: "mt",
        },
      ],
    },
    {
      id: 2,
      name: "LIST 2",
      materials: [],
    },
    {
      id: 3,
      name: "LIST 3",
      materials: [],
    },
  ],
};

const timeRanges = ["1w", "1m", "3m", "1y", "5y", "Max"];

const SectorMaterialsList = () => {
  const { t, i18n } = useTranslation();
  const { sectorId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch all sectors to get the sector image
  const { data: sectors } = useSectors({
    enabled: true,
    cache: true,
    cacheTTL: 30 * 60 * 1000, // 30 minutes
  });

  // Find current sector data to get image
  const currentSector = useMemo(() => {
    if (!sectors || !sectorId) return null;
    return sectors.find(s => s.id === parseInt(sectorId));
  }, [sectors, sectorId]);

  // Sektorname aus der Navigation, sonst (Direktaufruf/Neuladen) aus der Sektorliste.
  const sectorName =
    translateSectorLabel(location.state?.sectorName || currentSector?.name, i18n.resolvedLanguage || i18n.language) ||
    t("charts.sector.defaultName");

  // Get sector image with fallback
  const sectorImage = currentSector?.image || placeholderImage;

  const [selectedTimeRange, setSelectedTimeRange] = useState("1w");
  const [expandedLists, setExpandedLists] = useState([]);
  const didInitExpandedRef = useRef(false);
  // Fetch sector materials from API (single call; use timeframes from response for range switching)
  const { data: apiMaterialsData, loading, error } = useSectorMaterials(sectorId, {
    enabled: true,
    cache: true,
    cacheTTL: 10 * 60 * 1000, // 10 minutes
  });

  // Fallback to mock data when API is disabled or fails
  const materialsData = apiMaterialsData || mockMaterialsData;

  // Expand all material list sections by default once data is available.
  useEffect(() => {
    if (didInitExpandedRef.current) return;
    const lists = materialsData?.lists;
    if (!Array.isArray(lists) || lists.length === 0) return;
    setExpandedLists(lists.map((_, idx) => idx));
    didInitExpandedRef.current = true;
  }, [materialsData]);

  // Reset expansion state when switching sectors.
  useEffect(() => {
    didInitExpandedRef.current = false;
    setExpandedLists([]);
  }, [sectorId]);

  const handleMaterialClick = (material) => {
    // Navigate to material detail page using material code
    if (material && material.code) {
      // Track material view (fire-and-forget, don't wait for response)
      interactionService.trackMaterialView(material.id);

      // Navigate to material detail page
      navigate(`/charts/material/${material.code}`);
    }
  };

  const toggleList = (listIndex) => {
    setExpandedLists((prev) =>
      prev.includes(listIndex)
        ? prev.filter((idx) => idx !== listIndex)
        : [...prev, listIndex]
    );
  };

  const parseValue = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      let cleaned = v
        .trim()
        .replace(/\s/g, '')
        .replace(/[^\d,.\-]/g, '');

      const hasComma = cleaned.includes(',');
      const hasDot = cleaned.includes('.');
      if (hasComma && hasDot) {
        if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      } else if (hasComma) {
        const parts = cleaned.split(',');
        const trailing = parts[parts.length - 1] || '';
        if (parts.length === 2 && trailing.length <= 3) {
          cleaned = cleaned.replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      }

      const num = parseFloat(cleaned);
      return Number.isFinite(num) ? num : null;
    }
    return null;
  };

  const formatValue = (v, suffix) => {
    if (v === null || typeof v === 'undefined') return '—';
    const formatted = v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  // Werte in Originalwaehrung/-einheit der Reihe. Die fruehere clientseitige
  // Umrechnung (ein aktueller Kurs fuer alle Tage, externer Kursdienst) ist
  // entfallen; /sectors/{id}/materials bietet (noch) keine Umrechnung an.
  // Reihen ohne Waehrung (GWh, Index, %) zeigen nur ihre Einheit.
  const formatEntry = (rawVal, currency, unitRaw) => {
    const cur = formatCurrency(currency);
    const unit = formatUnit(unitRaw);
    const unitLabel = formatPerUnit(cur, unit);
    const num = parseValue(rawVal);
    if (num === null) return { display: rawVal ?? '—', unit: unitLabel || '—' };
    return {
      display: formatValue(num, cur || unit),
      unit: unitLabel || '—',
    };
  };

  const getTimeframeEntry = (material) => {
    const key = (selectedTimeRange || '').toLowerCase();
    return (
      material.formattedTimeframes?.[key] ||
      material.formattedTimeframes?.['1m'] ||
      material.formattedTimeframes?.['3m'] ||
      material.formattedTimeframes?.['max'] ||
      null
    );
  };

  return (
    <div className="sector-materials-page">
      {/* Sector Header with Image */}
      <div className="sector-header">
        <div
          className="sector-header-image"
          style={{
            backgroundImage: `url(${sectorImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <div
            className="sector-header-content"
            style={{
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '5px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <h1 style={{
              color: 'white',
              fontSize: '2.5rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
              margin: 0,
            }}>
              {sectorName}
            </h1>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="sector-controls">
        <div className="time-range-selector">
          {timeRanges.map((range) => (
            <button
              key={range}
              className={`time-range-btn ${
                selectedTimeRange === range ? "active" : ""
              }`}
              onClick={() => setSelectedTimeRange(range)}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Material Lists */}
      <div className="materials-lists">
        {loading && (
          <div className="loading-state">
            <p>{t("charts.overview.loadingMaterials")}</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>{t("charts.overview.errorLoadingMaterials", { message: error.message })}</p>
          </div>
        )}

        {!loading && !error && materialsData && materialsData.lists && materialsData.lists.length > 0 ? (
          materialsData.lists.map((list, listIndex) => (
            <div key={listIndex} className="material-list-section">
              <div
                className="material-list-header"
                onClick={() => toggleList(listIndex)}
              >
                <h3>{list.name}</h3>
                <button
                  type="button"
                  className={`toggle-icon ${expandedLists.includes(listIndex) ? "expanded" : ""}`}
                  aria-label={expandedLists.includes(listIndex) ? t("common.collapse") : t("common.expand")}
                  aria-expanded={expandedLists.includes(listIndex)}
                />
              </div>

              {expandedLists.includes(listIndex) && (
                <div className="material-list-content">
                  {list.materials && list.materials.length > 0 ? (
                    <table className="materials-table">
                      <thead>
                        <tr>
                          <th>{t("search.table.name")}</th>
                          <th>{t("search.table.date")}</th>
                          <th>%-Change</th>
                          <th>{t("search.table.value")}</th>
                      <th>{t("charts.sector.table.high")}</th>
                      <th>{t("charts.sector.table.low")}</th>
                      <th>{t("charts.sector.table.currencyUnit")}</th>
                    </tr>
                  </thead>
                  <tbody>
                        {list.materials.map((material, materialIndex) => {
                          const tfEntry = getTimeframeEntry(material) || {};
                          const dateDisplay = tfEntry.dateDisplay || material.date;
                          const changePct = tfEntry.change_pct ?? material.change_pct;
                          const changeDisplay = tfEntry.changeDisplay || material.change || t("common.na");
                          // Ohne Waehrung kein Waehrungs-Fallback
                          const entryCurrency = tfEntry.currency || material.currency || '';
                          const entryUnit = tfEntry.unit || tfEntry.value_unit || material.value_unit || material.unit || '';
                          const valueRaw = tfEntry.last_value ?? material.last_value ?? material.value;
                          const highRaw = tfEntry.high ?? material.high ?? material.highDisplay;
                          const lowRaw = tfEntry.low ?? material.low ?? material.lowDisplay;
                          const changeClass =
                            changePct === undefined || changePct === null || changePct >= 0
                              ? 'change-positive'
                              : 'change-negative';

                          return (
                            <tr
                              key={material.code || materialIndex}
                              className="material-row"
                              onClick={() => handleMaterialClick(material)}
                            >
                              <td className="material-name-cell">{material.name}</td>
                              <td>{dateDisplay}</td>
                              <td className={changeClass}>
                                {changeDisplay}
                              </td>
                              <td className="nowrap-cell">{formatEntry(valueRaw, entryCurrency, entryUnit).display}</td>
                              <td className="nowrap-cell">{formatEntry(highRaw, entryCurrency, entryUnit).display}</td>
                              <td className="nowrap-cell">{formatEntry(lowRaw, entryCurrency, entryUnit).display}</td>
                              <td className="nowrap-cell">{formatEntry(valueRaw, entryCurrency, entryUnit).unit}</td>
                            </tr>
                          );
                        })}
                     </tbody>
                    </table>
                  ) : (
                    <div className="empty-list">{t("charts.sector.noMaterialsInList")}</div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          !loading && !error && (
            <div className="empty-state">
              <p>{t("charts.sector.noMaterialsForSector")}</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SectorMaterialsList;
